import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { AvatarCircle } from '@/components';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IMessageResident } from '@/types/admin/messages';

interface GroupChatConflictNoticeProps {
  mismatchedGuarantors?: IMessageResident[];
}

export const GroupChatConflictNotice: React.FC<GroupChatConflictNoticeProps> = ({ mismatchedGuarantors }) => {
  const [open, setOpen] = useState(false);

  const count = useMemo(() => mismatchedGuarantors?.length || 0, [mismatchedGuarantors]);
  if (!count) return null;

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.headerRow} onPress={() => setOpen(true)} activeOpacity={0.8}>
        <Text style={styles.title}>Group chat conflict</Text>
        <View style={styles.countPill}>
          <Text style={styles.countText}>{count}</Text>
          <Text style={styles.chevron}>
            {'>'}
          </Text>
        </View>
      </TouchableOpacity>
      <Text style={styles.subtitle}>
        Due to privacy reasons, you can’t add a resident’s guarantors to a group chat with other residents. Guarantors will receive separate messages.
      </Text>

      <Modal visible={open} animationType="fade" transparent={true} onRequestClose={() => setOpen(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Conflict details</Text>
              <TouchableOpacity onPress={() => setOpen(false)}>
                <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              <Text style={styles.modalIntro}>The following guarantors cannot be added to the group chat with other residents.</Text>
              <View style={styles.listWrap}>
                {mismatchedGuarantors?.map((g) => {
                  const lc = g.leaseCustomers?.[0] as any;
                  const displayName = lc?.first_name ? `${lc.first_name} ${lc.last_name}` : lc?.email;
                  const status = (g.statusType || '').toString();
                  const statusFormatted = status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : '';
                  const initials = (lc?.first_name?.[0] || lc?.email?.[0] || '?').toUpperCase();
                  return (
                    <View key={g.id} style={styles.itemRow}>
                      <View style={styles.simpleAvatar}><Text style={styles.simpleAvatarText}>{initials}</Text></View>
                      <View style={styles.itemTextWrap}>
                        <Text style={styles.itemTitle} numberOfLines={1}>{displayName}</Text>
                        <Text style={styles.itemSubtitle} numberOfLines={1}>Guarantor - {statusFormatted}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingTop: 8,
    marginBottom: 16,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 6,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  countPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  countText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  chevron: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginLeft: 2,
  },
  subtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 18,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  modalBody: {
    maxHeight: 420,
    minWidth: 320,
  },
  modalBodyContent: {
    padding: 16,
  },
  modalIntro: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 12,
  },
  listWrap: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    padding: 12,
    gap: 12,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  itemTextWrap: {
    flex: 1,
  },
  simpleAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  simpleAvatarText: {
    fontSize: 12,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  itemSubtitle: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
});

export default GroupChatConflictNotice;


